import SVG from "react-inlinesvg";
import { cn } from "src/lib/cn";
import { SudoTooltip } from "src/sudokit/components/SudoTooltip";
import ImageShimmerOverlay from "./ImageShimmerOverlay";
import { useFadeInImage } from "./lib/useFadeInImage";

interface EntityImageThumbnailProps {
  size: "sm" | "md" | "lg";
  imageUrl?: string | null;
  imageFailed: boolean;
  onImageError: () => void;
  isGenerating: boolean;
  readOnly: boolean;
  onClick?: () => void;
  ariaLabel: string;
  tooltipText: string;
  tooltipDisabled: boolean;
  className?: string;
  wrapperClassName?: string;
}

export default function EntityImageThumbnail({
  size,
  imageUrl,
  imageFailed,
  onImageError,
  isGenerating,
  readOnly,
  onClick,
  ariaLabel,
  tooltipText,
  tooltipDisabled,
  className,
  wrapperClassName,
}: EntityImageThumbnailProps) {
  const iconSize = size === "lg" ? 68 : size === "md" ? 32 : 24;
  const iconSrc = size === "lg" ? "/assets/image-slim.svg" : "/assets/image.svg";
  const hasImage = !!imageUrl && !imageFailed;
  const { loaded: imageLoaded, ref: imageRef, onLoad: handleImageLoad } = useFadeInImage(hasImage ? imageUrl : null);

  const content = (
    <>
      {hasImage ? (
        <img
          ref={imageRef}
          src={imageUrl!}
          alt=""
          loading="lazy"
          decoding="async"
          className={cn("h-full w-full object-cover will-change-transform [transition:transform_650ms_cubic-bezier(0.25,0.46,0.45,0.94),opacity_300ms_ease] group-hover:scale-[1.08] motion-reduce:[transition:opacity_300ms_ease]", imageLoaded ? "opacity-100" : "opacity-0")}
          aria-hidden="true"
          onError={onImageError}
          onLoad={handleImageLoad}
        />
      ) : size === "lg" ? (
        // Only the large cover gets the raised icon "card" (it scales/rotates on hover); smaller
        // sizes render the bare glyph so it doesn't read as a second frame inside the tile.
        <span className="entity-image-icon entity-image-icon--lg">
          <SVG
            src={iconSrc}
            width={iconSize}
            height={iconSize}
            style={{ width: iconSize, height: iconSize }}
            aria-hidden="true"
          />
        </span>
      ) : (
        <SVG
          src={iconSrc}
          width={iconSize}
          height={iconSize}
          style={{ width: iconSize, height: iconSize }}
          aria-hidden="true"
        />
      )}
      {isGenerating && <ImageShimmerOverlay />}
    </>
  );

  const imageClassName = cn("entity-image group", size === "lg" && "entity-image--lg", !hasImage && "entity-image--cta", isGenerating && "relative", className);

  return (
    <SudoTooltip
      text={tooltipText}
      disabled={tooltipDisabled}
      triggerClassName={wrapperClassName}
    >
      {readOnly ? (
        <span className={imageClassName}>{content}</span>
      ) : (
        <button
          type="button"
          className={cn(imageClassName, "use-component-styles")}
          onClick={onClick}
          aria-label={ariaLabel}
        >
          {content}
        </button>
      )}
    </SudoTooltip>
  );
}
